/** @jsx jsx */
import { jsx } from 'theme-ui';
import { Fragment, memo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Box, Badge, Flex, Text, Heading, Spacer, Stack, Divider } from '@chakra-ui/core';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { FaChevronDown } from 'react-icons/fa';
import { AppointmentStatus } from '../../../firebase/firestore/documents/appointmentStatus';
import { AppointmentType, AvailableBookingWithPosition } from '../../../firebase/firestore/documents/appointmentAvaiability';
import { getTranslatedValue } from '../../../utils/localizedString';
import { useStoreState } from '../../../models/hooks';
import { RemoteConfigKey } from '../../../firebase/remoteConfig';
import colors from '../../../styles/colors';
import { InviteCollaboratorsView } from '../collaborators/InviteCollaboratorsView';
import { ReactComponent as _PositionStateDot } from '../../../assets/img/position-state.svg';
import { InterviewTimezoneTime } from '../availabilityCanendar/InterviewTimezoneTime';
import { useSeekerExperiences } from './useSeekerExperiences';
import { Experience } from '../../messaging/seeker/candidate/profile/components/Experience';
import { MESSAGING_CANDIDATE_TABS } from '../../../routes/constants';
import useBusinessLocation from './useLocation';
import { useCurrentUserProfile } from '../../../app/hooks/useCurrentUserProfile';
import { ScheduleIcon } from '../ScheduleIcon';

const PositionStateDot = memo(_PositionStateDot);

export const BookingDetailContent = ({
  bookingDetail,
  isInline,
}: {
  bookingDetail: AvailableBookingWithPosition;
  isInline?: boolean;
}) => {
  const { t, i18n } = useTranslation('calendar');
  const [accordionOpen, setAccordionOpen] = useState(true);

  const seekerExperiences = useSeekerExperiences(bookingDetail?.candidate?.seeker);
  const { currentUserProfile } = useCurrentUserProfile();
  const businessLocation = useBusinessLocation(bookingDetail.booking.businessId);

  const enableCollaborators: boolean = useStoreState((state) => {
    return state.remoteConfig[RemoteConfigKey.ENABLE_COLLABORATOR_FEATURE];
  });

  const isPassedInterview = () => {
    const parsedDateTime = moment(bookingDetail.booking.date, 'YYYY-MM-DD HH:mm:ss');
    const newDateTime = parsedDateTime.add(bookingDetail.booking.duration, 'minutes');
    const currentDateTime = moment();
    if (newDateTime.isBefore(currentDateTime)) {
      return true;
    }
    return false;
  };

  const isRequested = bookingDetail.booking.accepted === false || bookingDetail.booking.shortCode === AppointmentStatus.requested;

  if (isInline) {
    return (
      <Fragment>
        <Box p={4} mx={4} mb={4} border="1px solid" borderColor="#E5E7EB" borderRadius="lg">
          <Flex alignItems="center">
            <Box className="icon-block" mr={3} mt="2px">
              <ScheduleIcon
                type={bookingDetail.booking.type as AppointmentType}
                isAdhoc={bookingDetail.candidate.appointment?.adhoc}
                bookingId={bookingDetail.booking.id || /* istanbul ignore next */ ''}
              />
            </Box>
            <Box>
              <Flex align="center" mb={1}>
                <Text fontWeight="bold" fontSize="sm" lineHeight={1} mr={2}>
                  {getTranslatedValue(bookingDetail.candidate.jobTitle, i18n.language)}
                </Text>
                {isRequested && (
                  <Badge bg={colors.orange50} color={colors.orangeDark} textTransform="none" borderRadius="md" px={2} py={0.5}>
                    {t('calendar:booking.notAcceptedYet')}
                  </Badge>
                )}
              </Flex>
              <Text className="candidate-name" color="orange.500" fontSize="0.65rem" fontWeight="normal" mb={1} lineHeight={1}>
                {`${bookingDetail.candidate.firstName} ${bookingDetail.candidate.lastName}`}
              </Text>
              <Text className="candidate-name" fontSize="0.65rem" fontWeight="normal" mb={1} lineHeight={1}>
                {bookingDetail.candidate.businessName}
              </Text>
              <InterviewTimezoneTime
                startTime={bookingDetail.booking.date}
                endTime={moment(bookingDetail.booking.date).add(bookingDetail.booking.duration, 'minutes').toDate()}
                isShort
                size="compact"
              />
              {bookingDetail.candidate.dismissed && (
                <Flex alignItems="center">
                  <PositionStateDot color={colors.red[600]} />
                  <Text
                    fontSize="xs"
                    color={colors.red[600]}
                    lineHeight="shorter"
                    pt={1}
                    maxW={{ base: '11rem', md: '12rem', lg: '18rem' }}
                  >
                    {t('candidateStatus:DISMISSED')}
                  </Text>
                </Flex>
              )}
            </Box>
          </Flex>
        </Box>

        {enableCollaborators && !bookingDetail.candidate.dismissed && !isPassedInterview() && (
          <Fragment>
            <Divider borderColor="#EBEDF3" m={0} />
            <Box p={4}>
              <InviteCollaboratorsView bookingDetail={bookingDetail} />
            </Box>
          </Fragment>
        )}
      </Fragment>
    );
  }

  return (
    <Fragment>
      <Box className="booking-title" borderColor="#EBEDF3" borderBottomWidth="1px" p="4">
        <Text fontSize="sm">{bookingDetail.candidate.businessName}</Text>
        <Text fontSize="xs" fontWeight="bold">
          {businessLocation?.completeAddress}
        </Text>
        <Flex align="center">
          <Heading as="h4" size="xs" color={`${colors.blue.default} !important`} mr={2}>
            {getTranslatedValue(bookingDetail.candidate.jobTitle, i18n.language)}
          </Heading>
          {isRequested && (
            <Badge bg={colors.orange50} color={colors.orangeDark} textTransform="none" borderRadius="md" px={2} py={0.5}>
              {t('calendar:booking.notAcceptedYet')}
            </Badge>
          )}
        </Flex>
        <InterviewTimezoneTime
          startTime={bookingDetail.booking.date}
          endTime={moment(bookingDetail.booking.date).add(bookingDetail.booking.duration, 'minutes').toDate()}
        />
        {bookingDetail.candidate.dismissed && (
          <Flex alignItems="center">
            <PositionStateDot color={colors.red[600]} />
            <Text
              fontSize="xs"
              color={colors.red[600]}
              lineHeight="shorter"
              pt={1}
              maxW={{ base: '11rem', md: '12rem', lg: '18rem' }}
            >
              {t('candidateStatus:DISMISSED')}
            </Text>
          </Flex>
        )}
      </Box>
      <Box p="0px 15px">
        <Box borderColor="#EBEDF3" borderBottomWidth="1px" p="15px 0">
          <Heading as="h4" size="xs">
            {t('eventForm.interviewLabel')}
          </Heading>
          {bookingDetail.candidate.appointment?.adhoc ? (
            <Flex>
              <Text fontSize="sm">{t(`common:appointmentTypes.${bookingDetail.booking.type}`)}</Text>
              <Text fontSize="sm" fontStyle="italic" color={colors.gray[500]} ml={1}>
                {t('availabilityDetail.onDemand')}
              </Text>
            </Flex>
          ) : (
            <Text fontSize="sm">{t(`common:appointmentTypes.${bookingDetail.booking.type}`)}</Text>
          )}
        </Box>
        {bookingDetail.booking.type === AppointmentType.inPerson && (
          <Box borderColor="#EBEDF3" borderBottomWidth="1px" p="15px 0">
            <Heading as="h4" size="xs">
              {t('interview.location')}
            </Heading>
            <Text fontSize="sm">{bookingDetail.booking.location}</Text>
          </Box>
        )}
        <Box borderColor="#EBEDF3" borderBottomWidth="1px" p="15px 0">
          <Heading as="h4" size="xs">
            {t('eventForm.interviewerLabel')}
          </Heading>
          <Text fontSize="sm">{bookingDetail.booking.recruiterName || ''}</Text>
        </Box>
        <Flex borderColor="#EBEDF3" borderBottomWidth="1px" p="15px 0">
          <Box>
            <Heading as="h4" size="xs">
              {t('eventForm.nameLabel')}
            </Heading>
            <Text fontSize="sm">{`${bookingDetail.candidate.firstName} ${bookingDetail.candidate.lastName}`}</Text>
          </Box>
          <Spacer />
          {currentUserProfile?.id === bookingDetail.booking.recruiterId && (
            <Text fontSize="xs" className="communicate-badge" color="#4A5568">
              <Link to={MESSAGING_CANDIDATE_TABS(bookingDetail.candidate.id, 'messaging')}>{t('eventForm.sendMessage')}</Link>
            </Text>
          )}
        </Flex>

        {seekerExperiences && seekerExperiences.hasExperiences && seekerExperiences.experiences && (
          <Box borderColor="#EBEDF3" borderBottomWidth="0">
            <Box m="0px -15px" className={/* istanbul ignore next */ accordionOpen ? 'accordion-opened' : 'accordion-collapsed'}>
              <Box
                p="15px"
                d="flex"
                alignItems="center"
                onClick={/* istanbul ignore next */ () => setAccordionOpen(!accordionOpen)}
              >
                <Box flex="1" textAlign="left">
                  <Heading as="h4" size="xs">
                    {t('eventForm.experienceLabel')}
                  </Heading>
                  <Text fontSize="sm">{t('interview.longDuration')}</Text>
                </Box>
                <Box className="drop-arrow">
                  <FaChevronDown />
                </Box>
              </Box>
              <Box className="experience-block">
                <Box>
                  <Stack spacing={2}>
                    {seekerExperiences.experiences.map((experience, index) => (
                      <Experience
                        key={experience.id}
                        experience={experience}
                        dataIndex={index}
                        totalItems={seekerExperiences.experiences.length}
                        paddingTop={2}
                        displayBusiness
                      />
                    ))}
                  </Stack>
                </Box>
              </Box>
            </Box>
          </Box>
        )}
      </Box>
      {enableCollaborators && !bookingDetail.candidate.dismissed && !isPassedInterview() && (
        <Fragment>
          <Divider borderColor="#EBEDF3" m={0} />
          <Box p="15px">
            <InviteCollaboratorsView bookingDetail={bookingDetail} />
          </Box>
        </Fragment>
      )}
    </Fragment>
  );
};

BookingDetailContent.defaultProps = {
  isInline: false,
};
